import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  useEffect(() => {
    if (token && user) {
      if (user.role === 'delivery_boy') navigate('/delivery/dashboard');
      else navigate('/dashboard');
    }
  }, [token, user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(loginData.email, loginData.password);
      toast.success('Login successful!');
      if (userData.role === 'delivery_boy') navigate('/delivery/dashboard');
      else navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-slate-50 to-violet-50 px-4" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Dot grid */}
      <div className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #CBD5E1 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-10">
          <img src="/logo.png" alt="JustFold" className="h-14 mx-auto mb-5 object-contain" />
          <h1 className="text-2xl font-black text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Portal</h1>
          <p className="text-slate-500 text-sm">Laundry Management System</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl p-8 shadow-xl shadow-slate-200/80 border border-slate-100">
          <div className="mb-6">
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Welcome back</h2>
            <p className="text-sm text-slate-400 mt-1">Enter your credentials to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
              <input
                id="login-email"
                type="email"
                placeholder="you@example.com"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
                data-testid="login-email-input"
                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none"
              />
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
              <input
                id="login-password"
                type="password"
                placeholder="••••••••"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                data-testid="login-password-input"
                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-xl transition-all hover:shadow-lg hover:shadow-blue-200 hover:-translate-y-0.5 text-sm"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Login to Dashboard <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-slate-400">Contact your administrator for access.</p>
          <button onClick={() => navigate('/')} className="text-xs text-slate-400 hover:text-blue-600 transition-colors">← Home</button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
